import { useState, useMemo } from "react";
import PropTypes from "prop-types";
import classNames from "classnames";
import { useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import { Check } from "lucide-react";

import { ModernPlanCardContainer } from "./ModernPlanCard.styles";
import { Loader } from "components/Loader";
import { contains, getPlanShortName } from "utils";
import { userDetailSelect } from "Layout/slice/selector";
import { BILLING_INTERVAL, PLAN_ACTION } from "utils/constants";
import { PlanConfirmationModal } from "features/Account/v2/ChoosePlanModal/PlanConfirmationModal";
import { DowngradeConfirmModal } from "features/Account/v2/ChoosePlanModal/DowngradeConfirmModal";

const PLAN_TIER = { free: 0, core: 1, plus: 2, pro: 3 };

const getPlanTier = (planId = "") => {
  const id = planId.toLowerCase();
  if (id.includes("free")) return PLAN_TIER.free;
  if (id.includes("core")) return PLAN_TIER.core;
  if (id.includes("plus")) return PLAN_TIER.plus;
  if (id.includes("pro") || id.includes("premium")) return PLAN_TIER.pro;
  return -1;
};

export const ModernPlanCard = ({
  plan,
  duration = "monthly",
  planCheckoutId = "",
  handlePlanSubscription = null,
  index = 0,
  strikePrice = null,
  upcomingPlanId = null,
  periodEnd = null,
  currentPlanName = null,
  onDowngradeToFree = null,
  freeDowngradeScheduled = false,
}) => {
  const [searchParams] = useSearchParams();
  const discountCodeParam = searchParams.get("discount_code");
  const userDetails = useSelector(userDetailSelect);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showDowngradeModal, setShowDowngradeModal] = useState(false);

  const currentPlan = userDetails?.surmount_plan;
  const stripeStatus = userDetails?.stripe_subscription?.status;
  const subscriptionExpired =
    stripeStatus === "canceled" || (!currentPlan?.id && !stripeStatus);
  let currentPlanId = currentPlan?.id;

  if (
    currentPlan &&
    contains(currentPlan.name?.toLowerCase() || "", "premium")
  ) {
    currentPlanId = currentPlan.id.replace("premium", "pro");
  }

  const action = useMemo(() => {
    const isFree = plan?.interval === null;
    const isActivePlan =
      plan.id === currentPlanId || (isFree && subscriptionExpired);
    if (isActivePlan) return PLAN_ACTION.CURRENT;
    if (plan.id === upcomingPlanId || (isFree && freeDowngradeScheduled))
      return PLAN_ACTION.SCHEDULED;
    if (isFree) return PLAN_ACTION.DOWNGRADE_FREE;

    const cardTier = getPlanTier(plan.id);
    const userTier = getPlanTier(currentPlanId || "free");
    if (cardTier < userTier) return PLAN_ACTION.DOWNGRADE_TIER;
    // Same-tier interval switch: to yearly = pay more (immediate proration),
    // to monthly = pay less (scheduled for period end).
    if (cardTier === userTier)
      return plan.interval === BILLING_INTERVAL.YEARLY
        ? PLAN_ACTION.SWITCH_UP
        : PLAN_ACTION.SWITCH_DOWN;
    // Higher tier: an active paid sub prorates immediately; free/expired checks out.
    return userTier > 0 && !subscriptionExpired
      ? PLAN_ACTION.UPGRADE
      : PLAN_ACTION.CHECKOUT;
  }, [
    plan,
    currentPlanId,
    upcomingPlanId,
    subscriptionExpired,
    freeDowngradeScheduled,
  ]);

  const shortName = getPlanShortName(plan.name);
  const isOrderDetails =
    action === PLAN_ACTION.UPGRADE || action === PLAN_ACTION.SWITCH_UP;
  const isDisabled = !!planCheckoutId || action === PLAN_ACTION.SCHEDULED;

  const getButtonLabel = () => {
    switch (action) {
      case PLAN_ACTION.CURRENT:
        return "Current plan";
      case PLAN_ACTION.SCHEDULED:
        return "Downgrade scheduled";
      case PLAN_ACTION.DOWNGRADE_FREE:
        return "Downgrade to Free";
      case PLAN_ACTION.DOWNGRADE_TIER:
        return `Downgrade to ${shortName}`;
      case PLAN_ACTION.SWITCH_DOWN:
        return "Switch to monthly";
      case PLAN_ACTION.SWITCH_UP:
        return "Switch to yearly";
      default:
        return `Upgrade to ${shortName}`;
    }
  };

  const getButtonClass = () => {
    if (action === PLAN_ACTION.CURRENT) return "current-plan";
    if (action === PLAN_ACTION.SCHEDULED) return "downgrade-scheduled";
    return "action-plan";
  };

  const prevTierName = useMemo(() => {
    const id = plan.id?.toLowerCase() || "";
    if (id.includes("core")) return "Free";
    if (id.includes("plus")) return "Core";
    if (id.includes("pro") || id.includes("premium")) return "Plus";
    return null;
  }, [plan.id]);

  const handleButtonClick = () => {
    if (planCheckoutId) return;
    if (
      action === PLAN_ACTION.DOWNGRADE_FREE ||
      action === PLAN_ACTION.DOWNGRADE_TIER ||
      action === PLAN_ACTION.SWITCH_DOWN
    ) {
      setShowDowngradeModal(true);
    } else if (
      action === PLAN_ACTION.UPGRADE ||
      action === PLAN_ACTION.SWITCH_UP ||
      action === PLAN_ACTION.CHECKOUT
    ) {
      setShowConfirmModal(true);
    }
  };

  const handleConfirmSubscription = async () => {
    if (handlePlanSubscription) {
      const successMessage =
        action === PLAN_ACTION.SWITCH_UP
          ? "Switched to yearly billing"
          : action === PLAN_ACTION.UPGRADE
          ? `Upgraded to ${plan.name}`
          : undefined;
      await handlePlanSubscription(plan.id, { successMessage });
    }
    setShowConfirmModal(false);
  };

  const handleConfirmDowngrade = async () => {
    if (action === PLAN_ACTION.DOWNGRADE_FREE) {
      if (onDowngradeToFree) await onDowngradeToFree();
    } else if (handlePlanSubscription) {
      const successMessage =
        action === PLAN_ACTION.SWITCH_DOWN
          ? "Switch to monthly billing scheduled"
          : `Downgrade to ${plan.name} scheduled`;
      await handlePlanSubscription(plan.id, { successMessage });
    }
    setShowDowngradeModal(false);
  };

  const originalPrice = plan.price?.toString() || "0";
  const discountPrice = plan.discounted_price?.toString() || "0";

  const splitOrgPrice = originalPrice ? originalPrice.split(".") : ["0"];
  const splitDiscountPrice = discountPrice ? discountPrice.split(".") : ["0"];

  const headerColorClass = useMemo(() => {
    const id = plan.id?.toLowerCase() || "";
    if (id.includes("free")) return "header-silver";
    if (id.includes("core")) return "header-green";
    if (id.includes("plus")) return "header-blue";
    if (id.includes("pro") || id.includes("premium")) return "header-warm";
    return "header-purple";
  }, [plan.id]);

  return (
    <ModernPlanCardContainer
      className={`plan-card ${headerColorClass}`}
      $index={index}
    >
      <div className="plan-header">
        <h3 className="plan-name">{plan.name}</h3>

        <div className="pricing-section">
          {"price" in plan && (
            <div className="price-display">
              {strikePrice && !discountCodeParam && (
                <span className="price-amount strike-price">
                  ${strikePrice}
                </span>
              )}
              <span
                className={classNames("price-amount", {
                  "line-through": discountCodeParam,
                })}
              >
                ${splitOrgPrice[0]}
                {splitOrgPrice[1] && splitOrgPrice[1] !== "00" && (
                  <span className="dot-price">.{splitOrgPrice[1]}</span>
                )}
              </span>
              {discountCodeParam && (
                <span className="price-amount discounted">
                  ${splitDiscountPrice[0]}
                  {splitDiscountPrice[1] && (
                    <span className="dot-price">.{splitDiscountPrice[1]}</span>
                  )}
                </span>
              )}
              {plan?.interval ? (
                <span className="price-period">
                  USD /{" "}
                  {duration === BILLING_INTERVAL.YEARLY ? "year" : "month"}
                </span>
              ) : (
                <span className="price-period free-label">Free forever</span>
              )}
            </div>
          )}

          {contains(plan.id, "professional") && (
            <>
              <span className="price-amount custom">Custom</span>
              <div className="custom-description">
                Tools for financial advisors, trading groups and more!
              </div>
            </>
          )}
        </div>
      </div>

      <div className="button-section">
        <button
          className={`plan-button ${getButtonClass()}`}
          onClick={handleButtonClick}
          disabled={isDisabled}
        >
          {planCheckoutId === plan.id && <Loader className="button-loader" />}
          <span className="button-text">{getButtonLabel()}</span>
        </button>
      </div>

      <div className="card-divider" />

      <div className="benefits-section">
        <p className="benefits-header">
          {prevTierName ? `Everything in ${prevTierName} and:` : "Includes:"}
        </p>

        <ul className="benefits-list">
          {plan.benefits?.map((benefit, idx) => (
            <li key={idx} className="benefit-item">
              <div className="check-icon">
                <Check size={14} strokeWidth={2.5} />
              </div>
              <div className="benefit-content">
                {benefit.type === "special-text" && (
                  <span
                    className="benefit-tag"
                    style={{
                      color: "black",
                      backgroundColor: benefit.tag?.background_color,
                    }}
                  >
                    {benefit.tag?.label}
                  </span>
                )}
                <span className="benefit-text">
                  {benefit.text}
                  {idx === 0 && <span className="benefit-asterisk">*</span>}
                </span>
              </div>
            </li>
          )) || []}
        </ul>
      </div>

      <PlanConfirmationModal
        show={showConfirmModal}
        onHide={() => setShowConfirmModal(false)}
        onConfirm={handleConfirmSubscription}
        plan={plan}
        duration={duration}
        isPaidUpgrade={isOrderDetails}
        isIntervalSwitch={action === PLAN_ACTION.SWITCH_UP}
      />

      <DowngradeConfirmModal
        show={showDowngradeModal}
        onHide={() => setShowDowngradeModal(false)}
        onConfirm={handleConfirmDowngrade}
        plan={plan}
        currentPlanName={currentPlanName || currentPlan?.name}
        periodEnd={periodEnd}
        duration={duration}
        isFreeDowngrade={action === PLAN_ACTION.DOWNGRADE_FREE}
        isIntervalSwitch={action === PLAN_ACTION.SWITCH_DOWN}
      />
    </ModernPlanCardContainer>
  );
};

ModernPlanCard.propTypes = {
  plan: PropTypes.object.isRequired,
  duration: PropTypes.string,
  planCheckoutId: PropTypes.string,
  handlePlanSubscription: PropTypes.func,
  index: PropTypes.number,
  strikePrice: PropTypes.number,
  upcomingPlanId: PropTypes.string,
  periodEnd: PropTypes.string,
  currentPlanName: PropTypes.string,
  onDowngradeToFree: PropTypes.func,
  freeDowngradeScheduled: PropTypes.bool,
};
